import {
  cn,
  HorizontalAutoScroll,
  loadDomMax,
  supportsHover as supportsHoverHelper,
  useIsHorizontalOverflow,
  useScrollOverflowMask,
  useScrollProgress,
} from '@bridebook/aura';
import { cva } from 'class-variance-authority';
import { LazyMotion, useMotionValueEvent } from 'motion/react';
import * as m from 'motion/react-m';
import {
  ComponentProps,
  ForwardedRef,
  forwardRef,
  useCallback,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronLeft, ChevronRight } from '../../icons';
import { scrollByOffset } from '../../lib/utils/scroll-by-offset';
import { ScrollChevronButton } from './scroll-chevron-button';
import { TabsProvider, type TabsSize } from './tabs-context';

const innerWrapperVariants = cva('', {
  variants: {
    variant: {
      primary: 'gap-0',
      secondary: 'gap-2',
    },
  },
});

type TabsBaseProps = {
  children: React.ReactNode;
  /**
   * Additional CSS classes to apply to the root element
   */
  className?: string;
  /**
   * Additional CSS classes to apply to component's children.
   */
  classNames?: {
    innerWrapper?: string;
    outerWrapper?: string;
    extraBottomBorder?: string;
  };
  /**
   * Additional props to pass to the HorizontalAutoScroll component.
   * By default we set alwaysScrollCenter to true. It helps users to discover the next tabs easier.
   */
  horizontalAutoScrollProps?: ComponentProps<typeof HorizontalAutoScroll>;
  /**
   * Fade indicator. Mask on left and right to indicate that the container is scrollable and there is something more to discover.
   * Automatically appears while scrolling.
   * @default true
   */
  showFadeIndicator?: boolean;
  /**
   * Show scroll chevron icon buttons. Visible only while hovering over the tabs container. Only on desktop.
   * @default true
   */
  showScrollButtons?: boolean;
  /**
   * Is the current device supports hover events. Used to determine if the scroll chevron icon buttons should be shown.
   * In most cases you don't need to set this prop as it will be automatically determined based on the browser capabilities.
   */
  supportsHover?: boolean;
  /**
   * Enable horizontal auto-scroll when there are many tabs.
   * When enabled, the tabs list will automatically scroll to keep the active tab visible.
   * @default false
   */
  withHorizontalAutoScroll?: boolean;
  /**
   * The current active tab value
   */
  activeValue?: string;
  /**
   * Callback when the active tab value changes
   */
  onActiveValueChange?: (activeValue: string) => void;
};

type TabsPrimaryProps = TabsBaseProps & {
  /**
   * Visual variant of the tabs.
   */
  variant?: 'primary';
  /**
   * Enable layout animations for the active indicator using Motion.
   * @default true
   */
  animated?: boolean;
  /**
   * The size of the tabs. It's responsive by default.
   * If needed you can force it by setting it to 'mobile' or 'desktop'.
   */
  size?: TabsSize;
};

type TabsSecondaryProps = TabsBaseProps & {
  /**
   * Visual variant of the tabs.
   */
  variant: 'secondary';
  /**
   * Secondary variant has a single size.
   */
  size?: never;
  /**
   * Secondary variant does not use Motion animations.
   */
  animated?: never;
};

export type TabsProps = TabsPrimaryProps | TabsSecondaryProps;

/**
 * Root component for the tabs. Manages the state of which tab is active.
 *
 * @example
 * const [activeTab, setActiveTab] = useState('tab1');
 * return (
 *   <Tabs value={activeTab} onActiveValueChange={setActiveTab} layoutId="tabs-layout-id">
 *     <TabItem value="tab1">Tab 1</TabItem>
 *     <TabItem value="tab2">Tab 2</TabItem>
 *   </Tabs>
 * );
 */
export const Tabs = forwardRef<HTMLDivElement | null, TabsProps>(
  (
    {
      withHorizontalAutoScroll = false,
      animated: animatedProp = true,
      activeValue: value,
      onActiveValueChange,
      children,
      className,
      classNames,
      horizontalAutoScrollProps,
      showFadeIndicator: showFadeIndicatorProps = true,
      size: sizeProp = 'responsive',
      showScrollButtons = true,
      supportsHover: supportsHoverProp,
      variant = 'primary',
      ...props
    }: TabsProps,
    ref: ForwardedRef<HTMLDivElement | null>,
  ) => {
    const stableId = useId();
    const isPrimary = variant === 'primary';
    const animated = isPrimary && animatedProp;
    const size = isPrimary ? sizeProp : undefined;

    const layoutId = `tabs-${stableId}`;
    // Calculate active index for HorizontalAutoScroll
    // This is some magic to "convert" activeValue to activeIndex needed for HorizontalAutoScroll component. It reads the value from TabItem component.
    const activeIndex = useMemo(() => {
      if (!withHorizontalAutoScroll || value === undefined) return -1;
      const childrenArray = Array.isArray(children) ? children : [children];
      return childrenArray.findIndex((child) => {
        if (typeof child === 'object' && child !== null && 'props' in child) {
          // Check if child has a value prop (direct trigger)
          if (child.props?.value) {
            return child.props.value === value;
          }
          // Check if child has children that are triggers (wrapped in div)
          // This is to support nested triggers. Like if you need a div wrapper to add extra padding or notification dot.
          if (child.props?.children) {
            const nestedChild = child.props.children;
            if (typeof nestedChild === 'object' && nestedChild !== null && 'props' in nestedChild) {
              return nestedChild.props?.value === value;
            }
          }
        }
        return false;
      });
    }, [children, value, withHorizontalAutoScroll]);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const horizontalAutoScrollRef = useRef<HTMLDivElement>(null);
    const containerRef = withHorizontalAutoScroll ? horizontalAutoScrollRef : scrollContainerRef;
    const { scrollXProgress } = useScrollProgress({ container: containerRef });

    const extraMaskForBorders =
      variant === 'primary'
        ? `, linear-gradient(180deg, transparent calc(100% - 2px), #000 calc(100% - 2px))`
        : '';
    const maskImage = useScrollOverflowMask(scrollXProgress, {
      extraMaskLayers: extraMaskForBorders,
    });

    const isOverflow = useIsHorizontalOverflow(containerRef);

    useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(ref, () => {
      if (withHorizontalAutoScroll) {
        return horizontalAutoScrollRef.current;
      }
      return scrollContainerRef.current;
    }, [withHorizontalAutoScroll]);

    // Scroll edge detection for scroll buttons
    const [scrollEdge, setScrollEdge] = useState<'left' | 'right' | null>('left');
    useMotionValueEvent(scrollXProgress, 'change', (value: number) => {
      if (value <= 0.03) {
        setScrollEdge('left');
      } else if (value >= 0.97) {
        setScrollEdge('right');
      } else {
        setScrollEdge(null);
      }
    });

    // Logic for showing scroll buttons
    const supportsHoverLocal = supportsHoverHelper();
    const supportsHover = supportsHoverProp ?? supportsHoverLocal;
    const showScrollButtonsCondition = showScrollButtons && supportsHover && isOverflow;

    // Logic for showing fade indicator
    const showFadeIndicatorCondition = showFadeIndicatorProps && isOverflow;

    const handleScroll = useCallback(
      (direction: 'left' | 'right') => {
        const containerWidth = containerRef.current?.clientWidth;
        const scrollSize = containerWidth ? containerWidth * 0.5 : 200; // scroll by 50% of container width by default
        if (containerRef.current) {
          scrollByOffset(
            containerRef.current,
            direction === 'left' ? -scrollSize : scrollSize,
            'smooth',
          );
        }
      },
      [containerRef],
    );

    const handleScrollLeft = () => handleScroll('left');
    const handleScrollRight = () => handleScroll('right');

    return (
      <LazyMotion features={loadDomMax} strict>
        <TabsProvider
          value={{
            withHorizontalAutoScroll,
            activeValue: value,
            onActiveValueChange,
            animated,
            layoutId,
            size,
            variant,
          }}>
          <div className={cn('group relative', classNames?.outerWrapper)}>
            <m.div
              style={{
                maskImage: showFadeIndicatorCondition ? maskImage : undefined,
              }}
              className={cn('relative flex w-full', className)}
              {...props}>
              {withHorizontalAutoScroll ? (
                <HorizontalAutoScroll
                  ref={horizontalAutoScrollRef}
                  alwaysScrollCenter
                  activeIndex={activeIndex}
                  className={cn(innerWrapperVariants({ variant }), classNames?.innerWrapper)}
                  {...horizontalAutoScrollProps}>
                  {children}
                </HorizontalAutoScroll>
              ) : (
                <div
                  ref={scrollContainerRef}
                  className={cn(
                    innerWrapperVariants({ variant }),
                    'relative scrollbar-hidden flex w-full touch-pan-x overflow-x-auto overflow-y-hidden whitespace-nowrap',
                    classNames?.innerWrapper,
                  )}>
                  {children}
                </div>
              )}
              {/* Extra bottom border used if additional horizontal padding is needed */}
              {isPrimary && (
                <div
                  className={cn(
                    'absolute right-0 bottom-0 left-0 h-[1px] bg-space-15',
                    classNames?.extraBottomBorder,
                  )}
                />
              )}
            </m.div>
            {/* Scroll IconButtons with chevron icons */}
            {showScrollButtonsCondition && (
              <>
                <ScrollChevronButton
                  icon={ChevronLeft}
                  onClick={handleScrollLeft}
                  isScrolledToEdge={scrollEdge === 'left'}
                  className="left-1"
                />
                <ScrollChevronButton
                  icon={ChevronRight}
                  onClick={handleScrollRight}
                  isScrolledToEdge={scrollEdge === 'right'}
                  className="right-1"
                />
              </>
            )}
          </div>
        </TabsProvider>
      </LazyMotion>
    );
  },
);
Tabs.displayName = 'Tabs';
